<template>
  <div>
    <label
      v-if="translate(label)"
      class="input__label"
      :for="id"
    >
      {{ translate(label) }} <span v-if="block.is_required">*</span>
    </label>

    <input-datepicker
      class="margin__t--6"
      v-model="date"
      :id="id"
      :open-date="openDate"
      :show-time="showTime"
      :clearable="!block.is_required && !defaultValue"
      :required="block.is_required"
      :available-dates="availableDates"
      :v="validator"
    />
  </div>
</template>

<script setup>
import { computed, inject } from 'vue'
import { useModule } from '@/composables/app/useModule'
import { useProcedure, useProcedureBlockValidator } from '@/composables/modules/procedures'
import { useI18n } from '@/vendors/i18n'
import { format } from 'date-fns'
import { get as _get, set as _set } from 'lodash'

import InputDatepicker from '&/atoms/InputDatepicker'

const { block } = defineProps({
  block: { type: Object, required: true }
})

const procedure = inject('procedure')
const procedure_execution = inject('procedure_execution')

const { translate } = useI18n()
const { getFromProcedure } = useProcedure({ procedure })
const { id, label, model, attributes, options } = useModule({ component: block, parent: procedure_execution })
const { validationArgs, validator } = useProcedureBlockValidator({ validations: block.mandatory, state: model })

const defaultValue = computed(() => {
  if (attributes.value.default_option === 'specific_date') return attributes.value.default_value
  if (attributes.value.default_option === 'today') return new Date().toISOString()

  return getFromProcedure({ key: block.default_value })
})

const availableDates = computed(() => ({
  start: _get(validationArgs.value, 'minDate.$params.min'),
  end: _get(validationArgs.value, 'maxDate.$params.max')
}))

const openDate = computed(() => options.value.open_date || _get(availableDates.value, 'start') || new Date())

const dateFormat = computed(() => _get(options.value, 'format'))

const showTime = computed(() => {
  if (typeof options.value.show_time === 'string') return getFromProcedure({ key: options.value.show_time, default_value: true })

  return options.value.show_time
})

const date = computed({
  get: () => model.value,
  set: value => _set(procedure_execution.value, block.model, dateFormat.value ? format(new Date(value), dateFormat.value) : value)
})

if (!date.value && defaultValue.value) date.value = defaultValue.value
</script>
